(ns manage.ad-variant.creation.feed.db
  (:require ["moment-timezone$default" :as moment-tz]
            [clojure.spec.alpha :as spec]
            [clojure.string :as str]
            [common.form.core :as form]
            [common.loader.db :as loader.db]
            [common.util :as util]
            [common.validators.core :refer [internal-url? s3-url? url?]]
            [manage.resource.feed-imports :as feed-imports]
            [mate.re-frame :as mr]
            [re-frame.core :as rf]))

(def api-key-field-type-options
  [{:value "query-parameter" :label "Query parameter"}
   {:value "header-parameter" :label "Header parameter"}])

(def initial-value
  {:type "variant"
   :url ""

   :requires-auth? false
   :api-key-field-type "query-parameter"
   :api-key-field-name ""
   :api-key ""

   :schedule-import? false
   :time (.set (.utc moment-tz) #js {:hour 00 :minute 00})
   :timezone util/user-timezone})

(spec/def ::url
  (spec/and string?
            (complement str/blank?)
            (spec/or :s3 s3-url? :http url?)))

(spec/def ::api-key-field-name
  (spec/and string? (complement str/blank?)))

(spec/def ::api-key
  (spec/and string? (complement str/blank?)))

(defmulti value :requires-auth?)

(defmethod value :default []
  (spec/keys :req-un [::url]))

(defmethod value true []
  (spec/keys :req-un [::url
                      ::api-key-field-name
                      ::api-key]))

(spec/def ::value
  (spec/multi-spec value :requires-auth?))

(def validate-form
  (rf/enrich
   (form/get-validate-form-fn :form.ad-variants.feed ::value)))

(defn form-value->payload [{:keys [requires-auth? schedule-import? time] :as form-value}]
  (as-> (select-keys form-value [:id :url :type :name]) payload

        (if requires-auth?
          (merge payload (select-keys form-value [:api-key-field-type :api-key-field-name :api-key]))
          (merge payload {:api-key-field-type "none" :api-key-field-name nil}))

        (if schedule-import?
          (assoc payload :schedule (str "0 " (.minutes ^js time) " " (.hours ^js time) " * * ?"))
          (assoc payload :schedule nil))))

(defn response->form-value [{:keys [schedule api-key-field-type api-key-field-name] :as response}]
  (as-> (select-keys response [:id :url :type]) form-value

        (if (not= api-key-field-type "none")
          (merge form-value {:requires-auth? true
                             :api-key-field-type api-key-field-type
                             :api-key-field-name api-key-field-name})
          (merge form-value (select-keys initial-value [:requires-auth? :api-key-field-type :api-key-field-name])))

        (if (seq schedule)
          (merge form-value {:schedule-import? true
                             :time (let [[_s minutes hours _ _ _] (str/split schedule #" ")]
                                     (.set (.utc moment-tz) #js {:hour hours :minute minutes}))
                             :timezone (:timezone initial-value)})
          (merge form-value (select-keys initial-value [:schedule-import? :time :timezone])))))

(rf/reg-event-fx :form.ad-variants.feed/init-form
  [(rf/inject-cofx :sub :resource.adset/current-feed-id)
   (rf/inject-cofx :sub :resource.feed-importers/one)]
  (fn [{:keys [db]
        :resource.adset/keys [current-feed-id]
        :resource.feed-importers/keys [one]} _]
    (let [value (if current-feed-id (response->form-value (:value one)) initial-value)
          mode (if current-feed-id :edit :create)]
      {:db (-> db
               (assoc-in [:form.ad-variants.feed :meta :mode] mode)
               (assoc-in [:form.ad-variants.feed :meta :initial-url] (get-in one [:value :url])))
       :fx [[:dispatch [:form.ad-variants.feed/init value]]]})))

(rf/reg-event-fx :form.ad-variants.feed/submit-form
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   validate-form]
  (fn [{:keys [db resource.user/token url-context]} [_ form-value]]
    (let [mode (get-in db [:form.ad-variants.feed :meta :mode])
          payload (form-value->payload form-value)
          callbacks {:on-success [:form.ad-variants.feed/submit-success]
                     :on-failure [:form.ad-variants.feed/submit-form-failure]}]
      (-> {:db db}
          (loader.db/open-loader)
          (as-> effects
            (if (= :create mode)
              (feed-imports/http-post-feed effects url-context payload token callbacks)
              (feed-imports/http-patch-feed effects url-context payload token callbacks)))))))

(rf/reg-event-fx :form.ad-variants.feed/submit-success
  (fn [{:keys [db]} [_ response]]
    (let [mode (get-in db [:form.ad-variants.feed :meta :mode])]
      (-> {:db db}
          (feed-imports/on-feed-importers-one-success response)
          (loader.db/close-loader)
          (mr/conj-fx [:invalidate-queries {:queryKey ["feed-importer" (:advertiser-id response) (:id response)]}])
          (mr/conj-fx [:invalidate-queries {:queryKey ["feed-importer-fields" (:advertiser-id response) (:id response)]}])
          (mr/conj-fx [:invalidate-queries {:queryKey ["content-build-unique-values"
                                                       (:advertiser-id response) (:id response)]}])
          (mr/conj-fx [:dispatch [:modal/close]])
          (mr/conj-fx [:dispatch [:response-handler/handle-response
                                  {:entity-name "Feed"
                                   :type :success
                                   :message "Successfully saved feed"}]])
          (mr/conj-fx [:dispatch [:resource.feed-importers/get-fields (:id response)]])
          (mr/conj-fx (when (= mode :create)
                        [:dispatch [:resource.adset/patch {:feedId (:id response)}]]))))))

(rf/reg-event-fx :form.ad-variants.feed/submit-form-failure
  (fn [{:keys [db]} [event response]]
    (-> {:db db}
        (loader.db/close-loader)
        (mr/conj-fx [:dispatch [:response-handler/handle-response
                                {:entity-name "Feed"
                                 :type :error
                                 :event event
                                 :message "Failed to save feed"
                                 :response response}]]))))

(rf/reg-sub :form.ad-variants.feed/url-changed?
  :<- [:form.ad-variants.feed/value [:url]]
  :<- [:form.ad-variants.feed/meta [:initial-url]]
  (fn [[url initial-url]]
    (not= url initial-url)))

(rf/reg-sub :form.ad-variants.feed/external-url?
  :<- [:form.ad-variants.feed/value [:url]]
  (fn [url]
    ((every-pred seq url? (complement internal-url?)) url)))

(def use-error-message (partial form/use-error-message :form.ad-variants.feed))

(def use-bind-control (partial form/use-bind-control :form.ad-variants.feed))

(form/register-form {:namespace :form.ad-variants.feed
                     :initial-value initial-value
                     :validate-form validate-form
                     :submit-event :form.ad-variants.feed/submit-form})
